import {
    ArchiveOutlined,
    Delete,
    Edit,
    HistoryOutlined,
    LinkOff,
} from '@mui/icons-material';
import {
    Box,
    Chip,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import Swal from 'sweetalert2';
import api from '../../api';
import ActionBubbleTooltip from '../ActionBubbleTooltip';
import { ColorSwatchLabel } from '../HtmlColorField';
import FilamentStockBar from './FilamentStockBar';
import { filamentLabel } from '../../helpers/filaments';
import { getPrinterBrandPalette } from '../../helpers/printerBrand';

/**
 * Groups in-use filament rows by printer, ordered by printer registration
 * (oldest first). `printerMetaByUuid` maps printer uuid → { order, brand, name }.
 */
export function groupInUseFilamentsByPrinter(materials, printerMetaByUuid = {}) {
    const groups = new Map();
    (materials || []).forEach((material) => {
        (material.printers || []).forEach((printer) => {
            const key = printer.uuid;
            if (!groups.has(key)) {
                const meta = printerMetaByUuid[key] || {};
                const order = Number.isFinite(meta.order)
                    ? meta.order
                    : Number.MAX_SAFE_INTEGER;
                groups.set(key, {
                    printerUuid: printer.uuid,
                    printerName: printer.name || meta.name || printer.uuid,
                    printerBrand: meta.brand || '',
                    printerOrder: order,
                    rows: [],
                });
            }
            groups.get(key).rows.push({
                material,
                assignmentUuid: printer.assignment_uuid,
                location: printer.location,
                sortOrder: printer.sort_order,
            });
        });
    });
    return [...groups.values()]
        .sort((a, b) => {
            if (a.printerOrder !== b.printerOrder) {
                return a.printerOrder - b.printerOrder;
            }
            return String(a.printerName || '').localeCompare(String(b.printerName || ''), 'pt-BR', {
                sensitivity: 'base',
                numeric: true,
            });
        })
        .map((group) => ({
            ...group,
            rows: group.rows.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
        }));
}

function filamentCountLabel(count) {
    return count === 1 ? '1 filamento' : `${count} filamentos`;
}

export default function FilamentsInUseTable({
    materials,
    printerMetaByUuid,
    /** @deprecated use printerMetaByUuid */
    printerOrderByUuid,
    onChanged,
    onEdit,
    onViewUsage,
    onArchive,
    onDelete,
}) {
    const metaByUuid = useMemo(() => {
        if (printerMetaByUuid && Object.keys(printerMetaByUuid).length) {
            return printerMetaByUuid;
        }
        // Compat: só ordem (listagens antigas / testes).
        const legacy = {};
        Object.entries(printerOrderByUuid || {}).forEach(([uuid, order]) => {
            legacy[uuid] = { order };
        });
        return legacy;
    }, [printerMetaByUuid, printerOrderByUuid]);

    const groups = useMemo(
        () => groupInUseFilamentsByPrinter(materials, metaByUuid),
        [materials, metaByUuid],
    );

    const handleUnassign = async (row) => {
        const confirmed = await Swal.fire({
            title: 'Desvincular filamento?',
            text: `${filamentLabel(row.material)} será removido de ${row.printerName || 'esta impressora'}.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Desvincular',
            cancelButtonText: 'Cancelar',
        });
        if (!confirmed.isConfirmed) {
            return;
        }
        try {
            await api.delete(`printers/${row.printerUuid}/filaments`, {
                params: { assignment_uuid: row.assignmentUuid },
            });
            onChanged?.();
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Não foi possível desvincular',
                text: error?.response?.data?.error || 'Tente novamente.',
            });
        }
    };

    if (groups.length === 0) {
        return (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                    Filamentos em uso
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Nenhum filamento vinculado a impressoras no momento.
                </Typography>
            </Paper>
        );
    }

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                Filamentos em uso
            </Typography>
            <Stack spacing={2}>
                {groups.map((group) => {
                    const palette = getPrinterBrandPalette(group.printerBrand);
                    const brandBadge = palette.chip;

                    return (
                        <Paper
                            key={group.printerUuid}
                            variant="outlined"
                            sx={{
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderLeftWidth: 5,
                                borderLeftColor: palette.accent,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    px: 2,
                                    py: 1.25,
                                    bgcolor: palette.soft,
                                    borderBottom: `1px solid ${palette.accent}33`,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                    {brandBadge ? (
                                        <Chip label={group.printerName} size="small" {...brandBadge} />
                                    ) : (
                                        <Typography variant="subtitle2" fontWeight={800}>
                                            {group.printerName}
                                        </Typography>
                                    )}
                                    {group.printerBrand ? (
                                        <Typography variant="caption" color="text.secondary">
                                            {group.printerBrand}
                                        </Typography>
                                    ) : null}
                                </Box>
                                <Typography
                                    variant="caption"
                                    fontWeight={700}
                                    sx={{
                                        color: palette.accent,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.4,
                                    }}
                                >
                                    {filamentCountLabel(group.rows.length)}
                                </Typography>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Filamento</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Local</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Cor</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>Estoque</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Impressões</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Ações</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {group.rows.map((row, rowIndex) => (
                                            <TableRow
                                                key={`${row.assignmentUuid}-${row.material.id}`}
                                                hover
                                                sx={{
                                                    bgcolor: rowIndex % 2 === 0 ? 'transparent' : 'action.hover',
                                                    '&:last-of-type td': {
                                                        borderBottom: 0,
                                                    },
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {row.material.type}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {row.material.brand}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{row.location}</TableCell>
                                                <TableCell>
                                                    <ColorSwatchLabel
                                                        color={row.material.color}
                                                        label={row.material.color_label}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <FilamentStockBar
                                                        currentWeight={row.material.current_weight}
                                                        spoolWeight={row.material.spool_weight}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {(row.material.usage_count || 0) > 0 ? (
                                                        <Chip
                                                            label={row.material.usage_count}
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => onViewUsage?.(row.material)}
                                                        />
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">—</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <ActionBubbleTooltip title="Desvincular este filamento desta impressora">
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => handleUnassign({
                                                                ...row,
                                                                printerUuid: group.printerUuid,
                                                                printerName: group.printerName,
                                                            })}
                                                            aria-label="desvincular da impressora"
                                                        >
                                                            <LinkOff fontSize="small" />
                                                        </IconButton>
                                                    </ActionBubbleTooltip>
                                                    <Tooltip title="Histórico de uso">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => onViewUsage?.(row.material)}
                                                            aria-label="histórico de uso"
                                                        >
                                                            <HistoryOutlined fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => onEdit?.(row.material.id)}
                                                        aria-label="editar filamento"
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    {(row.material.usage_count || 0) > 0 ? (
                                                        <Tooltip title="Arquivar filamento">
                                                            <IconButton
                                                                size="small"
                                                                color="warning"
                                                                onClick={() => onArchive?.(row.material)}
                                                                aria-label="arquivar filamento"
                                                            >
                                                                <ArchiveOutlined fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Remover">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => onDelete?.(row.material)}
                                                                aria-label="remover material"
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    );
                })}
            </Stack>
        </Box>
    );
}
